<script lang="ts" setup>
const props = defineProps<{
  subtotal: number;
  deliveryFee: number;
  discountPerc?: number;
  discountValue?: number;
  serviceCharge: number;
  voucherCode?: string;
  voucherDiscount?: number;
  voucherValid?: boolean | string;
  voucherRemovable?: boolean;
  total: number;
  separator?: boolean;
}>();

const emits = defineEmits<{
  (e: 'click:removeVoucher'): void;
}>();

const textVoucherDiscount = computed<string>(() => {
  return props.voucherDiscount
    ? `- ${useCurrency().format(props.voucherDiscount)}`
    : '';
});

const textTotal = computed<string>(() => useCurrency().format(props.total));

interface SummaryItem {
  visible: boolean;
  label: string;
  value: string;
  key: string;
}

const summary = computed<SummaryItem[]>(() => [
  {
    visible: true,
    label: 'Subtotal',
    value: useCurrency().format(props.subtotal),
    key: 'subtotal'
  },
  {
    visible: !!props.deliveryFee,
    label: 'Delivery Fee',
    value: useCurrency().format(props.deliveryFee),
    key: 'delivery-fee'
  },
  {
    visible: !!props.discountValue,
    label: props.discountPerc
      ? `Discount ${props.discountPerc}%`
      : 'Discount',
    value: `- ${useCurrency().format(props.discountValue ?? 0)}`,
    key: 'discount'
  },
  {
    visible: !!props.serviceCharge,
    label: 'Service Charge',
    value: useCurrency().format(props.serviceCharge),
    key: 'service-charge'
  }
]);

const onRemoveVoucher = (): void => {
  emits('click:removeVoucher');
};
</script>

<template>
  <div
    class="summary"
    :class="{ 'pt-1': !props.separator }"
  >
    <template
      v-for="item of summary"
      :key="item.key"
    >
      <div
        v-if="item.visible"
        class="flex justify-between px-2"
        :class="{ 'border-b border-dashed border-inherit': props.separator }"
        :data-test="`${item.key}-container`"
      >
        <span :data-test="`${item.key}-label`">
          {{ item.label }}
        </span>
        <span :data-test="item.key">
          {{ item.value }}
        </span>
      </div>
    </template>

    <div
      v-if="voucherCode"
      class="summary__voucher items-center px-2"
      :class="{
        'border-b border-dashed border-inherit': props.separator,
        'pl-0': props.voucherRemovable
      }"
    >
      <div class="flex items-center gap-2">
        <UIcon
          v-if="props.voucherRemovable"
          class="text-2xl text-company-1 cursor-pointer"
          name="ic:round-remove-circle-outline"
          data-test="remove-voucher"
          @click="onRemoveVoucher()"
        />

        <span
          class="font-semibold"
          data-test="voucher-code"
        >
          {{ `Voucher (${props.voucherCode})` }}
        </span>
      </div>
      <span
        class="text-end"
        data-test="voucher-discount"
      >
        {{ textVoucherDiscount }}
      </span>
      <span
        v-if="props.voucherValid !== true"
        class="pl-8 text-xs text-red-500"
        data-test="voucher-validate"
      >
        {{ props.voucherValid }}
      </span>
    </div>

    <div
      class="flex justify-between px-2 text-xl leading-10 font-semibold"
      :class="{ 'border-t border-dashed border-inherit mt-1': !props.separator }"
    >
      <span>Total</span>
      <span data-test="total">
        {{ textTotal }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.summary {
  &__voucher {
    display: grid;
    grid-template-areas:
      'text value'
      'info info';

    &>div:first-of-type {
      grid-area: text;
    }

    &>span:nth-of-type(1) {
      grid-area: value;
    }

    &>span:nth-of-type(2) {
      grid-area: info;
    }
  }
}
</style>
